import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useLocalStorage } from 'react-use';

import { Button } from '@/components/form/baseInputs/Button';
import { IconButton } from '@/components/form/baseInputs/Button/IconButton';
import { BankIcon } from '@/components/icons/BankIcon';
import { Contract01Icon } from '@/components/icons/Contract01Icon';
import { PresentationHealthcare01Icon } from '@/components/icons/PresentationHealthcare01Icon';
import { PresentationSignature01Icon } from '@/components/icons/PresentationSignature01Icon';
import { PresentationWill01Icon } from '@/components/icons/PresentationWill01Icon';
import { TableIcon } from '@/components/icons/TableIcon';
import { Upload01Icon } from '@/components/icons/Upload01Icon';
import { WaterfallFilledIcon } from '@/components/icons/WaterfallFilledIcon';
import { XCloseIcon } from '@/components/icons/XCloseIcon';
import { LOCAL_STORAGE_KEYS } from '@/constants/localStorageKeys';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { useCanEditHouseholdData } from '@/modules/authentication/hooks/useCanEditHouseholdData';
import { HargroveConsultationButton } from '@/modules/estateInsights/MissingDocumentsPanel/HargroveConsultationButton';
import { MissingDocumentsContextProvider } from '@/modules/missingDocuments/contexts/MissingDocumentsContext';
import { useTrowser } from '@/modules/navigation/trowsers/trowser.context';
import { TrowserKey } from '@/modules/navigation/trowsers/trowser.types';
import { SmartReviewOnboardingSection } from '@/modules/onboarding/smartReview/SmartReviewOnboardingModal.types';
import { romie, ROMIE_BOLD_WEIGHT } from '@/styles/fonts/romie';
import { COLORS } from '@/styles/tokens/colors';
import { FONT_SIZING, TITLE_SERIF_SIZING } from '@/styles/tokens/fonts';

const DOCUMENT_TYPES = [
  { label: 'Trust documents', Icon: BankIcon },
  { label: 'Health care proxies', Icon: PresentationHealthcare01Icon },
  { label: 'Wills', Icon: PresentationWill01Icon },
  { label: 'Amendments and codicils', Icon: Contract01Icon },
  { label: 'Power of attorney', Icon: PresentationSignature01Icon },
  { label: 'Balance sheets', Icon: TableIcon },
];

export function HouseholdNoDocumentsBanner({
  householdId,
}: {
  householdId: string;
}) {
  const canEdit = useCanEditHouseholdData();
  const trowser = useTrowser(TrowserKey.SmartReviewOnboarding);
  const trackUserEvent = useTrackUserEvent();
  const storageKey = `${LOCAL_STORAGE_KEYS.HOUSEHOLD_NO_DOCUMENTS_DISMISSED}:${householdId}`;
  const [storedDismissal, persistDismissal] = useLocalStorage(
    storageKey,
    false
  );
  const [dismissedKey, setDismissedKey] = useState<string>();
  if (storedDismissal || dismissedKey === storageKey) return null;

  return (
    <Stack
      component="section"
      aria-label="Household documents"
      direction={{ xs: 'column', lg: 'row' }}
      spacing={4}
      sx={{
        position: 'relative',
        justifyContent: 'space-between',
        alignItems: { xs: 'stretch', lg: 'center' },
        p: { xs: 3, md: 4 },
        pr: { xs: 5, md: 8 },
        borderBottom: `1px solid ${COLORS.GRAY[100]}`,
        background:
          'linear-gradient(90deg, rgba(17,24,39,0) 5.593%, rgba(17,24,39,0.04))',
      }}
    >
      <Stack spacing={2} sx={{ flex: 1, minWidth: 0 }}>
        <Stack spacing={1.5} sx={{ maxWidth: 512 }}>
          <Typography
            component="h2"
            variant="title-l"
            sx={{
              fontFamily: romie.style.fontFamily,
              fontWeight: ROMIE_BOLD_WEIGHT,
              ...TITLE_SERIF_SIZING.xl,
            }}
          >
            This household has no documents
          </Typography>
          <Typography variant="body-m">
            Upload estate planning documents to get started, or create a
            waterfall from a template.
          </Typography>
        </Stack>
        {canEdit && (
          <Stack direction="row" gap={1.5} flexWrap="wrap" alignItems="center">
            <Button
              variant="primary"
              size="md"
              startIcon={Upload01Icon}
              iconProps={{ size: 16, 'aria-hidden': true }}
              sx={FONT_SIZING.xs}
              onClick={() => {
                trowser.open({ target: householdId });
                trackUserEvent(
                  'client_onboarding no_documents_banner_upload_clicked',
                  { householdId }
                );
              }}
            >
              Upload documents
            </Button>
            <Button
              variant="secondary"
              size="md"
              startIcon={WaterfallFilledIcon}
              iconProps={{ size: 16, 'aria-hidden': true }}
              sx={FONT_SIZING.xs}
              onClick={() => {
                trowser.open({
                  target: householdId,
                  section: SmartReviewOnboardingSection.NoDocumentsPlan,
                });
                trackUserEvent(
                  'client_onboarding no_documents_banner_template_clicked',
                  { householdId }
                );
              }}
            >
              Create waterfall from template
            </Button>
            <MissingDocumentsContextProvider>
              <HargroveConsultationButton size="md" />
            </MissingDocumentsContextProvider>
          </Stack>
        )}
      </Stack>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1,
          width: { xs: '100%', lg: 484 },
          flexShrink: 0,
        }}
      >
        {DOCUMENT_TYPES.map(({ label, Icon }) => (
          <Stack
            key={label}
            direction="row"
            spacing={1}
            alignItems="center"
            sx={{
              p: 1.5,
              bgcolor: COLORS.PRIMITIVES.WHITE,
              border: `1px solid ${COLORS.GRAY[200]}`,
              borderRadius: 1,
              boxShadow: '0px 3px 6px -3px rgba(17,24,39,0.1)',
            }}
          >
            <Icon size={20} color={COLORS.BLUE[500]} />
            <Typography variant="body-s">{label}</Typography>
          </Stack>
        ))}
      </Box>
      <IconButton
        icon={XCloseIcon}
        ariaLabel="Dismiss no-documents banner"
        variant="transparent"
        size="sm"
        sx={{ position: 'absolute', top: 1.5, right: 1.5 }}
        onClick={() => {
          // Keep dismissal usable even when browser storage is unavailable.
          setDismissedKey(storageKey);
          persistDismissal(true);
          trackUserEvent('client_onboarding no_documents_banner_dismissed', {
            householdId,
          });
        }}
      />
    </Stack>
  );
}
